import * as Sentry from '@sentry/vue'

const NOISE_PATTERNS: string[] = [
  // Native iOS WKWebView bridge (window.webkit.messageHandlers.*) — injected
  // by the native shell, not by our app code. Safe to ignore.
  'window.webkit.messageHandlers',
  'webkit.messageHandlers',
  'navigationPerformanceLogger',
  'WKWebView',
  'WKWebView API client did not respond',
  'setupIosCallbackHandler',
  'sendDataToNative',
  'sendPageHideMessage',
  'deallocated before the message',

  // Browser Background / Page Visibility API errors.
  'The requesting page is not visible',
  'Document is hidden',
  'background fetch',

  // Generic permission errors raised by browser APIs.
  'Permission was denied',

  // Chrome extension runtime errors in non-Chromium browsers.
  'runtime.sendMessage',
  'Tab not found',

  // localStorage SecurityErrors (guarded separately in src/stores/auth.ts).
  "Failed to read the 'localStorage' property",
  'The operation is insecure',

  // Mobile Safari script-load interruption (slow network, captive portal).
  'Importing a module script failed',

  // Third-party native-shell injection (Dynimac / similar).
  'SCDynimacBridge',

  // Stale assets / MIME-type mismatches on deployed CDN.
  'Unable to preload CSS',
  'is not a valid JavaScript MIME type',

  // Generic network errors outside our control.
  'Network Error',
  'Failed to fetch',

  // Unhandled promise rejections with no useful stacktrace.
  'captured as promise rejection',

  // Sentry-side payload warning, not an application bug.
  'Large HTTP payload',

  // LITECLIENT-10: 'ReferenceError: extDomain is not defined' raised inside
  // a minified bundle. 'extDomain' is not defined in our source; the
  // ReferenceError originates in a third-party / external script that we
  // cannot patch from the client.
  'extDomain is not defined',

  // LITECLIENT-P: 'Cannot read properties of null (reading \'emitsOptions\')'
  // raised inside Vue 3 internals (shouldUpdateComponent path). It fires
  // once per offending race, has no useful stack into our app code, and
  // Vue's component-update path is not patchable from userland.
  "Cannot read properties of null (reading 'emitsOptions')",

  // LITECLIENT-11: 'Failed to execute \'appendChild\' on \'Node\': Unexpected
  // token \'<\'' raised when a foreign HTML/JSON document is appended as
  // text. Common with third-party webview bridges and not actionable here.
  "Failed to execute 'appendChild' on 'Node'",

  // LITECLIENT-12: 'error loading dynamically imported module' is the same
  // family as 'Importing a module script failed' (already listed) but with
  // a different chrome/vite framing. Add the alternate phrasing.
  'error loading dynamically imported module',

  // LITECLIENT-16: 'UnavailableError: UnavailableError' raised from inside
  // the PayPal Honey Safari extension's injected script
  // (Honey.safariextension/h0.js). The error fires on a global unhandled
  // rejection with a stacktrace entirely inside the extension's own bundle;
  // we do not own the code and cannot patch it from the client. The
  // 'PayPal Honey' marker is the distinctive signal — it appears in the
  // stacktrace frame's filename/abs_path but not in the function name.
  'PayPal Honey',
]

function isNoise(event: Sentry.Event): boolean {
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      const haystacks = [
        ex.value,
        ex.type,
        ex.module,
        ex.stacktrace?.frames?.map((f) => f.function).join(' '),
        // Also include the frame filename/abs_path so we can suppress
        // errors whose only distinctive signal is the script path (e.g.
        // LITECLIENT-16, where the PayPal Honey extension throws
        // 'UnavailableError' from a minified h0.js with a meaningless
        // single-letter function name).
        ex.stacktrace?.frames
          ?.map((f) => `${f.filename ?? ''} ${f.abs_path ?? ''}`)
          .join(' '),
      ].filter((s): s is string => typeof s === 'string')
      for (const hay of haystacks) {
        if (NOISE_PATTERNS.some((p) => hay.includes(p))) return true
      }
    }
  }

  if (typeof event.message === 'string') {
    if (NOISE_PATTERNS.some((p) => (event.message as string).includes(p))) return true
  }

  if (event.breadcrumbs) {
    for (const b of event.breadcrumbs) {
      const hay = `${b.message ?? ''} ${b.category ?? ''} ${b.type ?? ''}`
      if (hay.trim() && NOISE_PATTERNS.some((p) => hay.includes(p))) return true
    }
  }

  return false
}

export function initSentry(app: any, router: any) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV || 'development',
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false
      }),
      Sentry.vueIntegration()
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (isNoise(event)) {
        return null
      }

      if (event.exception) {
        console.log('Sentry captured error:', event.exception.values?.[0]?.value)
      }
      return event
    }
  })
}
